import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import {
  quarterlyFeedback,
  RoutesEnum,
  ApiStatus,
  ToastTitle,
  HttpStatusCode,
} from '@app/shared/constants/global.enum';
import { FileUploadService } from '@app/shared/services/file-upload.service';
import { ToasterService } from '@app/shared/services/toaster.service';
import { getFieldError } from '@app/pages/hiring-manager/hiring-request/hiring-request.helper';
import { QuarterlyFeedback, VendorPositionsResponse, PeopleMetricsRequest } from '@app/shared/interfaces/quarterly-feedback.model';
import { getCurrentQuarter, getCurrentYear } from '@app/shared/utils/utils';
import { validateFile } from '@app/shared/utils/file-utils';
import { VendorQuarterlyFeedbackService } from './vendor-quarterly-feedback.service';
import { VendorService } from '@app/shared/services/vendor.service';
import { VendorDetails } from '@app/shared/interfaces/vendor.model';
import { quarterss } from '@app/shared/constants/constants';
@Component({
  selector: 'app-vendor-quarterly-feedback',
  templateUrl: './vendor-quarterly-feedback.component.html',
  styleUrls: ['./vendor-quarterly-feedback.component.scss'],
})
export class VendorQuarterlyFeedbackComponent implements OnInit {
    selectedFile: string | null = null;
    quarterlyFeedbackForm!: FormGroup;
    isSubmitted: boolean = false;
    fileError: string | null = null;
    fileSize: string | null = null;
    quarterlyFeedback = quarterlyFeedback;
    validationResult:any;
    excelFilePath:string = "assets/templates/learning-development-template.xlsx"
    routesEnum = RoutesEnum;
    vendorId: string = '';
    vendorPositionsData: { openedPositions: number, nicheSkills: number } | null = null;
    isLoading: boolean = true;

    // Constants for file upload controls
    private readonly uploadControlNames: string[] = [
      'supportingDocs',
      'sowUpload',
      'invoiceUpload',
      'learningDocs'
    ];

    peopleMetricsFields = [
      {
        label: quarterlyFeedback.openPositions,
        control: 'msaRateCard',
        disabled: true
      },
      {
        label: quarterlyFeedback.nicheSkills,
        control: 'assetCompliance',
        disabled: true
      },
      {
        label: quarterlyFeedback.fullTimeConversions,
        control: 'fullTimeConversions',
        disabled: false
      },
      {
        label: quarterlyFeedback.bufferResources,
        control: 'bufferResources',
        disabled: false
      }
    ];

    uploadedFileNames: { [key: string]: string } = {};
    @ViewChild('supportingDocsUpload') supportingDocsUpload!: any;
    @ViewChild('sowUploadFileUpload') sowUploadFileUpload!: any;
    @ViewChild('invoiceUploadFileUpload') invoiceUploadFileUpload!: any;
    @ViewChild('learningDocsUpload') learningDocsUpload!: any;

    selectedFiles: { [key: string]: File | null } = {
      supportingDocs: null,
      sowUpload: null,
      invoiceUpload: null,
      learningDocs: null,
    };
    fileErrors: { [key: string]: string } = {
      supportingDocs: '',
      sowUpload: '',
      invoiceUpload: '',
      learningDocs: '',
    };

    /**
     * Returns the list of upload control names used in the form
     * @returns array of control names for file uploads
     */
    private getUploadControlNames(): string[] {
      return [
        'supportingDocs',
        'sowUpload',
        'invoiceUpload',
        'learningDocs'
      ];
    }
    constructor(
      private quarterly: FormBuilder,
      private router: Router,
      private route: ActivatedRoute,
      private fileUploadService: FileUploadService,
      private toasterService: ToasterService,
      private vendorQuarterlyFeedbackService: VendorQuarterlyFeedbackService,
      private vendorService: VendorService,
    ) {}

    ngOnInit(): void {
      this.initializeForm();

      // First try to get vendorId from vendor service
      this.vendorService.getCurrentVendor().subscribe(
        (vendor: VendorDetails | null | undefined) => {
          if (vendor && vendor.id) {
            this.vendorId = vendor.id;
            this.fetchVendorPositionsData();
          } else {
            // If not available from service, check route params
            this.route.params.subscribe(params => {
              if (params['vendorId']) {
                this.vendorId = params['vendorId'];
                this.fetchVendorPositionsData();
              } else {
                // Check query parameters as last resort
                this.route.queryParams.subscribe(queryParams => {
                  if (queryParams['vendorId']) {
                    this.vendorId = queryParams['vendorId'];
                    this.fetchVendorPositionsData();
                  } else {
                    // If no vendor ID found, show error
                    this.toasterService.showError(quarterlyFeedback.noVendorId, ToastTitle.ERROR);
                  }
                });
              }
            });
          }
        },
        (error) => {
          this.toasterService.showError(quarterlyFeedback.vendorFetchError, ToastTitle.ERROR);

          // Fallback to route params
          this.route.params.subscribe(params => {
            if (params['vendorId']) {
              this.vendorId = params['vendorId'];
              this.fetchVendorPositionsData();
            }
          });
        }
      );
    }

    fetchVendorPositionsData(): void {
      this.isLoading = true;
      const { year, quarter } = this.getCurrentYearAndQuarter();
      this.vendorQuarterlyFeedbackService.getVendorOpenPositionsAndNicheSkills(this.vendorId, quarter, year)
        .subscribe(
          (response: VendorPositionsResponse) => {
            if (response.status === ApiStatus.SUCCESS && response.data) {
              this.vendorPositionsData = response.data;

              this.quarterlyFeedbackForm.get('msaRateCard')?.setValue(this.vendorPositionsData.openedPositions.toString());
              this.quarterlyFeedbackForm.get('assetCompliance')?.setValue(this.vendorPositionsData.nicheSkills.toString());

              this.quarterlyFeedbackForm.get('msaRateCard')?.disable();
              this.quarterlyFeedbackForm.get('assetCompliance')?.disable();
            } else {
              this.toasterService.showError(quarterlyFeedback.vendorDataFetchError, ToastTitle.ERROR);
            }
            this.isLoading = false;
          },
          (error) => {
            this.toasterService.showError(quarterlyFeedback.vendorDataFetchError, ToastTitle.ERROR);
            this.isLoading = false;
          }
        );
    }

    initializeForm(): void {
      this.quarterlyFeedbackForm = this.quarterly.group({
        msaRateCard: ['', Validators.required],
        assetCompliance: ['', Validators.required],
        fullTimeConversions: ['', Validators.required],
        bufferResources: ['', Validators.required],
        supportingDocs: ['', Validators.required],
        supportingDocsUrl: [''],
        sowCompliance: ['', Validators.required],
        invoiceCompliance: ['', Validators.required],
        sowUpload: ['', Validators.required],
        invoiceUpload: ['', Validators.required],
        learningDocs: ['', Validators.required],
      });
    }

    onFileSelected(file: File, controlName: string) {
      const validationResult = validateFile(file);

      if (!validationResult.isValid) {
        this.fileErrors[controlName] = validationResult.errorMessage;
        this.selectedFiles[controlName] = null;
        this.quarterlyFeedbackForm.get(controlName)?.setValue('');

        if (validationResult.fileSize) {
          this.fileSize = validationResult.fileSize;
        }
        return;
      }

      if (validationResult.fileSize) {
        this.fileSize = validationResult.fileSize;
      }
      this.selectedFiles[controlName] = file;
      this.fileErrors[controlName] = '';

      this.fileUploadService.uploadFile(file, controlName).subscribe((res) => {
        if (res?.success && res?.filePath) {
          this.quarterlyFeedbackForm.get(controlName)?.setValue(file);
          this.uploadedFileNames[controlName] = res?.filePath;
        } else {
          this.handleFileUploadError(res, controlName, res?.message || quarterlyFeedback.uploadFailedGeneric);
        }
        if (controlName === 'supportingDocs') {
          const urlValue = (res?.success && res?.filePath) ? res?.filePath : '';
          this.quarterlyFeedbackForm.get('supportingDocsUrl')?.setValue(urlValue);
        }
      });
    }


    onFileRemoved(controlName: string) {
      this.selectedFiles[controlName] = null;
      this.quarterlyFeedbackForm.get(controlName)?.setValue('');
      const fileUploadRefs: Record<string, { resetFileInput: () => void } | undefined> = {
        supportingDocs: this.supportingDocsUpload,
        sowUpload: this.sowUploadFileUpload,
        invoiceUpload: this.invoiceUploadFileUpload,
        learningDocs: this.learningDocsUpload,
      };
      const ref = fileUploadRefs[controlName];
      if (ref && typeof ref.resetFileInput === 'function') {
        ref.resetFileInput();
      }
    }

    getCurrentYearAndQuarter(): { year: number; quarter: string } {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth(); 
    const quarter = quarterss[Math.floor(month / 3)];
    return { year, quarter };
}

    onSave(): void {
      this.isSubmitted = true;

      this.uploadControlNames.forEach((controlName) => {
        if (!this.selectedFiles[controlName]) {
          this.fileErrors[controlName] = quarterlyFeedback.validationError;
        }
      });

      const form = this.quarterlyFeedbackForm;
      const filesValid = this.uploadControlNames.every(
        (control) => !!this.selectedFiles[control],
      );

      if (form.valid && filesValid) {
        const formValue = { ...form.getRawValue() };
        this.uploadControlNames.forEach((control) => {
          formValue[control] = this.uploadedFileNames[control] || '';
        });
        const { year, quarter } = this.getCurrentYearAndQuarter();
        const quarterInt = typeof quarter === 'string' && quarter.startsWith('Q') ? parseInt(quarter.substring(1), 10) : quarter;

        const peopleMetricsRequest = {
          vendorId: parseInt(this.vendorId),
          openedPositions: parseInt(formValue.msaRateCard),
          nicheSkills: parseInt(formValue.assetCompliance),
          numberOfFullTimeConversionsThisYear: parseInt(formValue.fullTimeConversions),
          bufferResources: parseInt(formValue.bufferResources),
          supportingDocumentsUrl: formValue.supportingDocsUrl || this.uploadedFileNames['supportingDocs'],
          investmentForStrategicPrograms: formValue.sowCompliance,
          investmentForStrategicProgramsUrl: this.uploadedFileNames['sowUpload'],
          numberOfUserCasesCoinnovated: parseInt(formValue.invoiceCompliance),
          numberOfUserCasesCoinnovatedUrl: this.uploadedFileNames['invoiceUpload'],
          learningAndDevelopment: this.uploadedFileNames['learningDocs'],
          yearId: year,
          quarterId: quarterInt
        };

        const file = this.selectedFiles['learningDocs'];
        if (file) {
          this.validationResult = validateFile(file);
        }

        if (this.validationResult.fileSize) {
          this.fileSize = this.validationResult.fileSize;
        }

        if (file) {
          this.vendorQuarterlyFeedbackService
            .saveFeedbackFormData(peopleMetricsRequest, file)
            .subscribe(
              (res) => {
                const filePath = res?.filePath || res?.data?.filePath;
                this.toasterService.showSuccess(
                  quarterlyFeedback.successMsg,
                  ToastTitle.SUCCESS
                );
                this.router.navigate([RoutesEnum.vendorFlowRoute]);
              },
              (error) => {
                const errors = error;
                this.toasterService.showError(quarterlyFeedback.errorMsg, ToastTitle.ERROR);
              },
            );
        }
      }
    }

    onCancel(): void {
      this.router.navigate([RoutesEnum.vendorFlowRoute]);
    }

    isFieldValid(fieldName: string): boolean {
      const control = this.quarterlyFeedbackForm.get(fieldName);
      if (this.isSubmitted || control?.touched) {
        return control?.valid ?? false;
      }
      return true;
    }

    getFieldError(fieldName: string): string {
      const control = this.quarterlyFeedbackForm.get(fieldName);
      return getFieldError(control);
    }

    navigateToVendorManagement(): void {
      this.router.navigate([RoutesEnum.vendorFlowRoute]);
    }

    private handleFileUploadError(error: any, controlName: string, errorMessage: string): void {
      this.isLoading = false;
      this.toasterService.showError(errorMessage, ToastTitle.ERROR);
      this.quarterlyFeedbackForm.get(controlName)?.setValue('');
      this.selectedFiles[controlName] = null;
      this.uploadedFileNames[controlName] = '';
    }
  }
